import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { bubbleAPI, setRailwayAuthToken, setBubbleAccessToken, setTokenRefreshCallback, BUBBLE_ENDPOINTS, notifyBubbleLogout } from '../services/api';
import { chooseStartupSession, isSessionRejected, readStoredSession, writeStoredSession } from './startupSession';
import logger from '../utils/logger';

export interface User {
  id: string;
  email?: string;
  name?: string;
  subscription?: any;
  hvacId?: string;
  hvacName?: string;
  subscription_status?: boolean;
  admin?: boolean;
  ThermCount?: number;
}

interface TokenData {
  accessToken: string;
  refreshToken: string;
  coreToken: string;
  coreTokenExp: number;
}

/**
 * How a sign-out happened. The dashboard's own Sign out button is the only
 * sign-out that should end the Bubble session too: an automatic one (a
 * session the dashboard could not restore) must leave Bubble alone, or a
 * dashboard hiccup inside the phone app logs the user out of the whole app.
 * `reason` stays on screen on the login page so the cause can be read.
 */
export interface LogoutOptions {
  reason?: string;
  notifyBubble?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  login: (userId: string, token?: string) => Promise<void>;
  logout: (options?: LogoutOptions) => void;
  isAuthenticated: boolean;
  getCoreToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tokens, setTokens] = useState<TokenData | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // PRIORITY 1: Check URL parameters first (for SSO/deep linking)
        // This allows switching users even if another user is already logged in
        const urlParams = new URLSearchParams(window.location.search);
        const userId = urlParams.get('user_id');
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');
        const coreToken = urlParams.get('core_token');
        const expiresAt = urlParams.get('expires_at');

        // If user is redirected from Bubble with tokens (SSO flow)
        // Core token is required, access_token is optional (will use core_token as fallback)
        if (userId && coreToken) {
          // Inside the Bubble app the iframe is reloaded with the tokens Bubble
          // stored at the first sign-in, long after the dashboard refreshed
          // them itself. A valid stored session for the same user that is at
          // least as fresh as the URL's tokens wins (src/contexts/startupSession.ts);
          // and if the URL's tokens fail, that stored session is the fallback
          // instead of a sign-out.
          const storedBefore = readStoredSession();
          const urlExp = expiresAt ? parseInt(expiresAt, 10) || null : null;
          const choice = chooseStartupSession({ userId, coreToken, expiresAt: urlExp }, storedBefore);
          if (choice === 'url') {
            logger.log('🔐 URL parameters detected - logging in as:', userId);
            try {
              await loginWithTokens(userId, accessToken, refreshToken, coreToken, expiresAt);
              // Clean up URL parameters after authentication (security)
              window.history.replaceState({}, document.title, window.location.pathname);
              return; // Exit early, don't check localStorage
            } catch (ssoErr) {
              if (!storedBefore || storedBefore.userId !== userId) throw ssoErr;
              logger.warn('⚠️  SSO tokens in the URL failed; falling back to the stored session for the same user:', (ssoErr as Error).message);
              writeStoredSession(storedBefore); // loginWithTokens may have overwritten it
            }
          } else {
            logger.log('🔐 URL tokens are no newer than the stored session for the same user; keeping the stored session');
          }
          window.history.replaceState({}, document.title, window.location.pathname);
          // Continue below: restore from localStorage.
        } else if (userId) {
          // Fallback: just user_id (legacy support)
          logger.log('🔐 URL user_id detected - logging in as:', userId);
          await login(userId);
          window.history.replaceState({}, document.title, window.location.pathname);
          return; // Exit early, don't check localStorage
        }

        // PRIORITY 2: Check localStorage for existing session (only if no URL params)
        const storedUserId = localStorage.getItem('smartfilterpro_user_id');
        const storedAccessToken = localStorage.getItem('smartfilterpro_access_token');
        const storedRefreshToken = localStorage.getItem('smartfilterpro_refresh_token');
        const storedCoreToken = localStorage.getItem('smartfilterpro_core_token');
        const storedCoreTokenExp = localStorage.getItem('smartfilterpro_core_token_exp');

        if (storedUserId && storedAccessToken && storedCoreToken) {
          logger.log('🔐 Restoring session from localStorage for user:', storedUserId);
          // Restore session from localStorage
          let effectiveAccessToken = storedAccessToken;
          let effectiveCoreToken = storedCoreToken;
          const tokenExpMs = parseInt(storedCoreTokenExp || '0', 10);

          // Check if tokens are expired or expiring soon
          const nowMs = Date.now();
          const fiveMinutesMs = 5 * 60 * 1000;
          const timeUntilExpiry = tokenExpMs - nowMs;
          let effectiveTokenExp = tokenExpMs;
          let tokensWereRefreshed = false;

          // Always attempt refresh if tokens are expired, expiring soon, or we haven't checked in a while
          // This ensures returning users get fresh tokens even after closing the browser
          const shouldRefresh = tokenExpMs === 0 || timeUntilExpiry < fiveMinutesMs;

          if (shouldRefresh) {
            logger.log('⚠️  Stored tokens are expired or expiring soon, attempting refresh before validation...');
            logger.log('📋 Token expiry analysis:', {
              tokenExpMs,
              nowMs,
              timeUntilExpiryMinutes: Math.floor(timeUntilExpiry / (60 * 1000)),
              isExpired: timeUntilExpiry <= 0
            });

            // Attempt to refresh tokens before proceeding
            if (storedRefreshToken && storedRefreshToken.trim()) {
              try {
                // Temporarily set tokens so we can make the refresh call
                setBubbleAccessToken(storedAccessToken);

                const response = await bubbleAPI.post(BUBBLE_ENDPOINTS.REFRESH_TOKEN, {
                  refresh_token: storedRefreshToken
                });

                const body = response.data?.response || response.data;

                if (body?.access_token) {
                  effectiveAccessToken = body.access_token;
                  tokensWereRefreshed = true;
                  logger.log('✅ Successfully refreshed tokens before session validation');

                  if (body.core_token) {
                    effectiveCoreToken = body.core_token;
                  }
                  if (body.expires_at) {
                    effectiveTokenExp = parseInt(String(body.expires_at), 10);
                  }

                  // Update localStorage with refreshed tokens
                  localStorage.setItem('smartfilterpro_access_token', effectiveAccessToken);
                  if (body.core_token) {
                    localStorage.setItem('smartfilterpro_core_token', effectiveCoreToken);
                  }
                  if (body.expires_at) {
                    localStorage.setItem('smartfilterpro_core_token_exp', String(body.expires_at));
                  }
                } else {
                  logger.warn('⚠️  Token refresh response missing access_token, proceeding with stored tokens');
                }
              } catch (refreshErr: any) {
                logger.error('❌ Failed to refresh expired tokens:', refreshErr.message);
                // Continue with stored tokens - the API call will fail and trigger proper logout
              }
            } else {
              logger.warn('⚠️  No refresh token available, proceeding with potentially expired tokens');
            }
          }

          const tokenData: TokenData = {
            accessToken: effectiveAccessToken,
            refreshToken: storedRefreshToken || '',
            coreToken: effectiveCoreToken,
            coreTokenExp: effectiveTokenExp
          };

          setTokens(tokenData);
          setRailwayAuthToken(effectiveCoreToken);
          setBubbleAccessToken(effectiveAccessToken);

          // Try to get cached user data as fallback
          const cachedUserData = localStorage.getItem('smartfilterpro_user_data');
          const cachedUser = cachedUserData ? JSON.parse(cachedUserData) : null;

          // Validate session by fetching user data with retry logic
          let retryCount = 0;
          const maxRetries = 2;
          let lastError: Error | null = null;

          while (retryCount <= maxRetries) {
            try {
              logger.log(`🔄 Attempt ${retryCount + 1}/${maxRetries + 1}: Fetching user data from Bubble...`);
              await login(storedUserId);
              logger.log('✅ Successfully fetched and validated user data');
              lastError = null; // Clear any previous error
              break; // Success, exit retry loop
            } catch (err) {
              lastError = err instanceof Error ? err : new Error('Unknown error');
              logger.warn(`⚠️  Attempt ${retryCount + 1} failed:`, lastError.message);

              retryCount++;
              if (retryCount <= maxRetries) {
                // Wait before retrying (exponential backoff: 500ms, 1000ms)
                const delay = 500 * retryCount;
                logger.log(`⏳ Waiting ${delay}ms before retry...`);
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            }
          }

          // If all retries failed, keep the session on cached user data unless
          // Bubble actually rejected it (401/404). A phone on a flaky
          // connection used to be signed out here on every failed reload;
          // the data refreshes on the next successful call.
          if (lastError) {
            const rejected = isSessionRejected(lastError);
            if (!rejected && cachedUser && cachedUser.id === storedUserId) {
              logger.warn(`⚠️ Could not fetch fresh user data (${lastError.message})${tokensWereRefreshed ? '; tokens were refreshed' : ''}`);
              logger.log('📦 Using cached user data to restore session:', cachedUser.email);

              const authenticatedUser: User = {
                id: cachedUser.id,
                email: cachedUser.email,
                name: cachedUser.name,
                subscription_status: cachedUser.subscription_status,
                admin: cachedUser.admin === true
              };

              setUser(authenticatedUser);
              // Session restored from cache - user data will refresh on next successful API call
            } else {
              logger.error(rejected
                ? '❌ Bubble rejected the stored session - signing out'
                : '❌ Could not reach Bubble and no cached user data to fall back on - signing out');
              throw lastError;
            }
          }
        }
      } catch (err) {
        logger.error('Failed to initialize authentication:', err);
        // Clear the invalid session; keep the reason on the login screen.
        logout({ reason: err instanceof Error ? err.message : 'Authentication failed', notifyBubble: false });
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login with tokens from URL (SSO from Bubble)
  const loginWithTokens = async (
    userId: string,
    accessToken: string | null,
    refreshToken: string | null,
    coreToken: string,
    expiresAt: string | null
  ) => {
    try {
      setLoading(true);
      setError(null);

      logger.log('🔗 SSO: Logging in with tokens from Bubble redirect');
      logger.log('📋 SSO Debug - Access Token:', accessToken ? `${accessToken.substring(0, 20)}...` : 'MISSING/EMPTY');
      logger.log('📋 SSO Debug - Refresh Token:', refreshToken ? `${refreshToken.substring(0, 20)}...` : 'MISSING/EMPTY');
      logger.log('📋 SSO Debug - Core Token:', coreToken ? `${coreToken.substring(0, 20)}...` : 'MISSING');
      logger.log('📋 SSO Debug - Expires At:', expiresAt || 'MISSING');
      logger.log('📋 SSO Debug - User ID:', userId);

      // Check if tokens are stale/expired
      // If tokens are expired or expiring soon (<5 minutes), attempt refresh before proceeding
      let shouldRefreshBeforeLogin = false;
      let effectiveAccessToken = accessToken || '';

      if (expiresAt) {
        const expiresAtMs = parseInt(expiresAt, 10);
        const nowMs = Date.now();
        const timeUntilExpiry = expiresAtMs - nowMs;
        const fiveMinutesMs = 5 * 60 * 1000;

        logger.log('📋 SSO Debug - Token expiry analysis:', {
          expiresAtMs,
          nowMs,
          timeUntilExpiryMinutes: Math.floor(timeUntilExpiry / (60 * 1000)),
          timeUntilExpiryHours: Math.floor(timeUntilExpiry / (60 * 60 * 1000)),
          timeUntilExpiryDays: Math.floor(timeUntilExpiry / (24 * 60 * 60 * 1000)),
          isExpired: timeUntilExpiry <= 0,
          isExpiringSoon: timeUntilExpiry > 0 && timeUntilExpiry < fiveMinutesMs
        });

        // If token is expired or expiring in <5 minutes, attempt refresh
        if (timeUntilExpiry < fiveMinutesMs) {
          shouldRefreshBeforeLogin = true;
          logger.log('⚠️  SSO: Tokens are expired or expiring soon - will attempt refresh before login');
        }
      }

      // If we need to refresh, attempt it now before making any API calls
      let effectiveCoreToken = coreToken;
      let effectiveExpiresAt = expiresAt;

      if (shouldRefreshBeforeLogin && refreshToken && refreshToken.trim()) {
        logger.log('🔄 SSO: Attempting to refresh expired tokens before login...');

        try {
          const response = await bubbleAPI.post(BUBBLE_ENDPOINTS.REFRESH_TOKEN, {
            refresh_token: refreshToken
          });

          const body = response.data?.response || response.data;

          if (body?.access_token) {
            effectiveAccessToken = body.access_token;
            logger.log('✅ SSO: Successfully refreshed tokens before login');
            logger.log('📋 SSO: New access token:', effectiveAccessToken.substring(0, 20) + '...');

            // Update core_token and expires_at if provided
            if (body.core_token) {
              effectiveCoreToken = body.core_token;
              logger.log('📋 SSO: Also received new core_token');
            }
            if (body.expires_at) {
              effectiveExpiresAt = String(body.expires_at);
              logger.log('📋 SSO: New expiry:', new Date(body.expires_at).toISOString());
            }
          } else if (response.data?.response?.status === 401) {
            logger.error('❌ SSO: Refresh failed - refresh_token is also expired');
            throw new Error('SSO tokens are expired and refresh_token is also invalid. Please generate a fresh SSO link from Bubble.');
          } else {
            logger.warn('⚠️  SSO: Refresh response did not include new access_token, using original token');
          }
        } catch (refreshErr: any) {
          logger.error('❌ SSO: Failed to refresh tokens before login:', refreshErr.message);
          throw new Error('SSO authentication failed: Tokens are expired and could not be refreshed. Please generate a fresh SSO link from Bubble.');
        }
      }

      logger.log('📋 SSO Debug - Using access token for Bubble API:', effectiveAccessToken ? `${effectiveAccessToken.substring(0, 20)}...` : 'MISSING');

      // Warn if refresh_token is missing (token refresh will fail later)
      if (!refreshToken || !refreshToken.trim()) {
        logger.warn('⚠️  SSO: No refresh_token provided in URL parameters - token refresh will not be possible');
        logger.warn('⚠️  SSO: Bubble redirect should include: ?user_id=...&access_token=...&refresh_token=...&core_token=...&expires_at=...');
      }

      // Store tokens (using effective values which may have been refreshed)
      const tokenData: TokenData = {
        accessToken: effectiveAccessToken,
        refreshToken: refreshToken || '',
        coreToken: effectiveCoreToken,
        coreTokenExp: effectiveExpiresAt ? parseInt(effectiveExpiresAt, 10) : 0
      };

      setTokens(tokenData);
      setRailwayAuthToken(effectiveCoreToken);
      setBubbleAccessToken(effectiveAccessToken);

      logger.log('✅ Tokens set in API clients');

      // Store in localStorage
      localStorage.setItem('smartfilterpro_user_id', userId);
      localStorage.setItem('smartfilterpro_access_token', effectiveAccessToken);
      localStorage.setItem('smartfilterpro_refresh_token', refreshToken || '');
      localStorage.setItem('smartfilterpro_core_token', effectiveCoreToken);
      localStorage.setItem('smartfilterpro_core_token_exp', effectiveExpiresAt || '0');

      // Create user from SSO data
      // Note: If Bubble has get_user_data and get_subscription endpoints,
      // you can uncomment the code below to fetch full user details
      const authenticatedUser: User = {
        id: userId,
        // These will be populated from Core-ingest API calls
        // or you can fetch from Bubble if endpoints exist
      };

      // Fetch user details from Bubble (if endpoint exists)
      // Using retry logic with extended timeout for SSO login
      const maxRetries = 3;
      const retryDelays = [2000, 4000, 8000]; // Exponential backoff: 2s, 4s, 8s
      const extendedTimeout = 30000; // 30 seconds for SSO login

      let getUserDataSuccess = false;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          if (attempt > 0) {
            const delay = retryDelays[attempt - 1];
            logger.log(`⏳ SSO: Retrying get_user_data (attempt ${attempt + 1}/${maxRetries + 1}) after ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }

          logger.log('📡 SSO: Fetching user data from Bubble for user:', userId);
          logger.log('📡 SSO: Bubble API URL:', bubbleAPI.defaults.baseURL);
          logger.log('📡 SSO: Endpoint:', BUBBLE_ENDPOINTS.GET_USER_DATA);
          logger.log('📡 SSO: Has Authorization header:', !!bubbleAPI.defaults.headers.common['Authorization']);
          logger.log('📡 SSO: Using extended timeout:', extendedTimeout + 'ms');

          const response = await bubbleAPI.post(
            BUBBLE_ENDPOINTS.GET_USER_DATA,
            { user_id: userId },
            { timeout: extendedTimeout }
          );

          logger.log('📥 SSO: get_user_data response received:', {
            status: response.status,
            hasData: !!response.data,
            hasResponse: !!response.data?.response,
            attempt: attempt + 1
          });

          if (response.data?.response) {
            const responseData = response.data.response;

            // Check if response is empty object/array (Bubble may return empty response for unauthorized access)
            const isEmptyResponse = (
              (typeof responseData === 'object' && Object.keys(responseData).length === 0) ||
              (Array.isArray(responseData) && responseData.length === 0)
            );

            // Check if this is an error response (Bubble returns 200 with nested error status)
            // OR if the response is completely empty (no data returned)
            if (responseData.status === 401 || responseData.status === 404 || isEmptyResponse) {
              const errorType = responseData.status === 401 ? 'soft 401' :
                               responseData.status === 404 ? 'soft 404' : 'empty response';
              logger.error(`🚫 SSO: Bubble returned ${errorType} in get_user_data:`, {
                status: responseData.status,
                body: responseData.Body,
                isEmptyResponse,
                responseKeys: Object.keys(responseData),
                attempt: attempt + 1
              });

              // If we have a refresh token and haven't tried refreshing yet, attempt to get valid token
              if (refreshToken && refreshToken.trim() && attempt === 0) {
                logger.log('🔄 SSO: Access token invalid - attempting to refresh before retry...');

                try {
                  const refreshResponse = await bubbleAPI.post(BUBBLE_ENDPOINTS.REFRESH_TOKEN, {
                    refresh_token: refreshToken
                  });

                  const refreshBody = refreshResponse.data?.response || refreshResponse.data;

                  if (refreshBody?.access_token) {
                    effectiveAccessToken = refreshBody.access_token;
                    logger.log('✅ SSO: Got new access token via refresh:', effectiveAccessToken.substring(0, 20) + '...');

                    // Update the API client with the new token
                    setBubbleAccessToken(effectiveAccessToken);

                    // Update localStorage
                    localStorage.setItem('smartfilterpro_access_token', effectiveAccessToken);

                    // Update core_token if provided
                    if (refreshBody.core_token) {
                      effectiveCoreToken = refreshBody.core_token;
                      localStorage.setItem('smartfilterpro_core_token', effectiveCoreToken);
                      setRailwayAuthToken(effectiveCoreToken);
                    }
                    if (refreshBody.expires_at) {
                      effectiveExpiresAt = String(refreshBody.expires_at);
                      localStorage.setItem('smartfilterpro_core_token_exp', effectiveExpiresAt);
                    }

                    // Continue to next iteration to retry with new token
                    logger.log('🔄 SSO: Retrying get_user_data with refreshed token...');
                    continue;
                  } else {
                    throw new Error('Refresh response did not include access_token');
                  }
                } catch (refreshErr: any) {
                  logger.error('❌ SSO: Token refresh failed:', refreshErr.message);
                  throw new Error(`SSO login failed: Access token invalid and refresh failed. ${refreshErr.message}`);
                }
              } else {
                // No refresh token or already retried - fail the login
                const errorMsg = responseData.status === 401
                  ? 'Access token expired or invalid'
                  : 'User not found';
                throw new Error(`SSO login failed: ${errorMsg}. Please try logging in again from Bubble.`);
              }
            }

            // If we get here, response contains actual user data
            const userData = responseData;
            authenticatedUser.email = userData.email;
            authenticatedUser.name = userData.name || userData.full_name || userData.username;
            authenticatedUser.subscription_status = userData.subscription_status;
            authenticatedUser.admin = userData.admin === true;
            authenticatedUser.ThermCount = userData.ThermCount;
            logger.log('✅ SSO: User data fetched successfully:', {
              email: userData.email,
              name: authenticatedUser.name,
              subscription_status: userData.subscription_status,
              admin: authenticatedUser.admin,
              attempt: attempt + 1
            });

            getUserDataSuccess = true;
            break; // Success - exit retry loop
          } else {
            logger.warn('⚠️  SSO: get_user_data returned empty response:', response.data);
            getUserDataSuccess = true; // Consider this success (empty but valid response)
            break;
          }
        } catch (err: any) {
          const isTimeout = err.message?.includes('timeout');
          const isNetworkError = err.message === 'Network Error';
          const shouldRetry = (isTimeout || isNetworkError) && attempt < maxRetries;

          logger.error(`❌ SSO: get_user_data endpoint failed (attempt ${attempt + 1}/${maxRetries + 1}):`, {
            message: err.message,
            status: err.response?.status,
            statusText: err.response?.statusText,
            data: err.response?.data,
            url: err.config?.url,
            isTimeout,
            isNetworkError,
            willRetry: shouldRetry
          });

          // Check if tokens are still available after the failed request
          // If tokens were cleared (e.g., by failed refresh), we should abort SSO login
          const hasTokensInStorage = localStorage.getItem('smartfilterpro_access_token') &&
                                       localStorage.getItem('smartfilterpro_core_token');

          if (!hasTokensInStorage) {
            logger.error('❌ SSO: Tokens were cleared during authentication, aborting login');
            throw new Error('SSO authentication failed: Token refresh failed during initial login. The tokens may be expired or invalid.');
          }

          // If this was the last attempt or error is not retryable, force logout for data accuracy
          // Do NOT continue with minimal data - user must have complete data from Bubble
          if (!shouldRetry) {
            const errorMsg = err.response?.status === 404
              ? 'User not found in Bubble - please contact support'
              : `Failed to fetch user data from Bubble after ${attempt + 1} attempts`;
            logger.error(`❌ SSO: ${errorMsg}`);
            throw new Error(errorMsg);
          }
          // Otherwise, continue to next retry iteration
        }
      }

      // Note: Subscription data is device-specific and will be fetched
      // by the Dashboard component for each device using getDeviceSubscription()

      setUser(authenticatedUser);

      // Cache user data in localStorage for fallback on reload
      if (authenticatedUser.email || authenticatedUser.name) {
        localStorage.setItem('smartfilterpro_user_data', JSON.stringify({
          id: authenticatedUser.id,
          email: authenticatedUser.email,
          name: authenticatedUser.name,
          subscription_status: authenticatedUser.subscription_status,
          admin: authenticatedUser.admin,
          cached_at: new Date().toISOString()
        }));
      }

      setLoading(false);

      logger.log('✅ SSO: Successfully logged in from Bubble (tokens validated by redirect)');
    } catch (err) {
      logger.error('SSO login failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to authenticate with provided tokens';
      setError(errorMessage);
      setLoading(false);
      throw new Error(errorMessage);
    }
  };

  // Login with email/password (Hubitat pattern)
  const loginWithPassword = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      // Call Bubble's ha_password_login endpoint
      const response = await bubbleAPI.post(BUBBLE_ENDPOINTS.LOGIN, {
        email: email.trim(),
        password: password
      });

      const body = response.data?.response || response.data;

      if (!body.access_token || !body.core_token) {
        throw new Error('Invalid login response from Bubble');
      }

      // Extract HVAC info
      const hvacIds = Array.isArray(body.hvac_id) ? body.hvac_id : [body.hvac_id];
      const hvacNames = Array.isArray(body.hvac_name) ? body.hvac_name : [body.hvac_name];
      const hvacId = hvacIds[0];
      const hvacName = hvacNames[0] || hvacId;

      // Store tokens
      const tokenData: TokenData = {
        accessToken: body.access_token,
        refreshToken: body.refresh_token || '',
        coreToken: body.core_token,
        coreTokenExp: body.expires_at || 0
      };

      setTokens(tokenData);
      setRailwayAuthToken(body.core_token);
      setBubbleAccessToken(body.access_token);

      // Store in localStorage
      localStorage.setItem('smartfilterpro_user_id', body.user_id);
      localStorage.setItem('smartfilterpro_access_token', body.access_token);
      localStorage.setItem('smartfilterpro_refresh_token', body.refresh_token || '');
      localStorage.setItem('smartfilterpro_core_token', body.core_token);
      localStorage.setItem('smartfilterpro_core_token_exp', String(body.expires_at || 0));

      // Get subscription data for the primary device
      let subscription = null;
      try {
        const subResponse = await bubbleAPI.post(BUBBLE_ENDPOINTS.GET_SUBSCRIPTION, {
          user_id: body.user_id,
          device_id: hvacId  // Include device_id for device-specific subscription
        });
        subscription = subResponse.data?.response;
      } catch (err) {
        logger.warn('Failed to fetch subscription data:', err);
      }

      // Fetch additional user data including subscription_status
      let userDataResponse = null;
      try {
        const response = await bubbleAPI.post(BUBBLE_ENDPOINTS.GET_USER_DATA, {
          user_id: body.user_id
        });
        userDataResponse = response.data?.response;
      } catch (err) {
        logger.warn('Failed to fetch user data:', err);
      }

      const authenticatedUser: User = {
        id: body.user_id,
        email: email,
        name: userDataResponse?.name || body.name || body.full_name || email.split('@')[0],
        subscription,
        hvacId,
        hvacName,
        subscription_status: userDataResponse?.subscription_status,
        admin: userDataResponse?.admin === true,
        ThermCount: userDataResponse?.ThermCount
      };

      setUser(authenticatedUser);

      // Cache user data in localStorage for fallback on reload
      localStorage.setItem('smartfilterpro_user_data', JSON.stringify({
        id: authenticatedUser.id,
        email: authenticatedUser.email,
        name: authenticatedUser.name,
        subscription_status: authenticatedUser.subscription_status,
        admin: authenticatedUser.admin,
        cached_at: new Date().toISOString()
      }));

      setLoading(false);
    } catch (err: any) {
      logger.error('Login failed:', err);
      let errorMessage: string;
      if (err.response?.status === 400 || err.response?.status === 401) {
        errorMessage = 'Email or password not found. Please try again.';
      } else {
        errorMessage = err instanceof Error ? err.message : 'Failed to authenticate user';
      }
      setError(errorMessage);
      setLoading(false);
      throw new Error(errorMessage);
    }
  };

  // Fallback login with just userId (for URL parameter flow)
  const login = async (userId: string, token?: string) => {
    try {
      setLoading(true);
      setError(null);

      logger.log('📡 Fetching user data from Bubble for user:', userId);

      // Validate user with Bubble API
      const response = await bubbleAPI.post(BUBBLE_ENDPOINTS.GET_USER_DATA, {
        user_id: userId
      });

      logger.log('📥 Bubble get_user_data response:', {
        hasData: !!response.data,
        hasResponse: !!response.data?.response,
        status: response.status
      });

      if (!response.data || !response.data.response) {
        throw new Error('Invalid user data received from Bubble - empty response');
      }

      const userData = response.data.response;

      // Log received user data for debugging
      logger.log('👤 Received user data:', {
        has_email: !!userData.email,
        has_name: !!userData.name,
        has_full_name: !!userData.full_name,
        has_username: !!userData.username,
        subscription_status: userData.subscription_status
      });

      // Validate that we have at least some identifying information
      if (!userData.email && !userData.name && !userData.full_name && !userData.username) {
        logger.error('❌ User data validation failed - no identifying information present');
        throw new Error('Incomplete user data received from Bubble - missing email and name');
      }

      // Note: Subscription data is device-specific and will be fetched
      // by the Dashboard component for each device using getDeviceSubscription()

      const authenticatedUser: User = {
        id: userId,
        email: userData.email,
        name: userData.name || userData.full_name || userData.username,
        subscription_status: userData.subscription_status,
        admin: userData.admin === true,
        ThermCount: userData.ThermCount
      };

      logger.log('✅ User authenticated:', {
        id: authenticatedUser.id,
        email: authenticatedUser.email,
        name: authenticatedUser.name,
        subscription_status: authenticatedUser.subscription_status,
        admin: authenticatedUser.admin
      });

      setUser(authenticatedUser);

      // Cache user data in localStorage for fallback on reload
      localStorage.setItem('smartfilterpro_user_data', JSON.stringify({
        id: authenticatedUser.id,
        email: authenticatedUser.email,
        name: authenticatedUser.name,
        subscription_status: authenticatedUser.subscription_status,
        admin: authenticatedUser.admin,
        cached_at: new Date().toISOString()
      }));

      // Store session
      localStorage.setItem('smartfilterpro_user_id', userId);
      if (token) {
        localStorage.setItem('smartfilterpro_token', token);
      }

      setLoading(false);
    } catch (err: any) {
      logger.error('❌ Login failed:', err);

      // Handle specific error codes with clear messages
      let errorMessage: string;
      if (err.response?.status === 404) {
        errorMessage = 'User not found in Bubble - session invalid. Please login again.';
        logger.error('🚫 404 Error: User data not found in Bubble - forcing re-login');
      } else if (err.response?.status === 401) {
        errorMessage = 'Session expired or invalid. Please login again.';
        logger.error('🔒 401 Error: Authentication failed - forcing re-login');
      } else {
        errorMessage = err instanceof Error ? err.message : 'Failed to authenticate user';
      }

      setError(errorMessage);
      setLoading(false);
      throw new Error(errorMessage);
    }
  };

  // Get core token with automatic refresh
  const getCoreToken = async (): Promise<string | null> => {
    if (!tokens) return null;

    const TOKEN_SKEW_MS = 60 * 1000; // 60 seconds buffer in milliseconds
    const nowMs = Date.now();

    // Check if token is still valid (expires_at is stored in milliseconds)
    if (tokens.coreToken && tokens.coreTokenExp && nowMs < (tokens.coreTokenExp - TOKEN_SKEW_MS)) {
      return tokens.coreToken;
    }

    // Token expired or about to expire, refresh it
    const refreshed = await forceRefreshTokens();
    if (refreshed) {
      // Prefer the core token from the refresh response; fall back to the
      // stored core token (kept when the refresh response omits core_token).
      return refreshed.coreToken || localStorage.getItem('smartfilterpro_core_token');
    }
    return null;
  };

  // Result of a successful token refresh.
  // - accessToken: the (possibly new) Bubble access token
  // - coreToken: the NEW core token from the refresh response, or null if the
  //   refresh response did not include a core_token (existing one was kept)
  interface RefreshResult {
    accessToken: string;
    coreToken: string | null;
  }

  // Force token refresh (used when Bubble API returns 401)
  const forceRefreshTokens = async (): Promise<RefreshResult | null> => {
    // Try to get tokens from state first, fallback to localStorage
    let currentTokens = tokens;

    if (!currentTokens) {
      logger.log('⚠️  Tokens not in state, checking localStorage...');
      const storedRefreshToken = localStorage.getItem('smartfilterpro_refresh_token');
      const storedAccessToken = localStorage.getItem('smartfilterpro_access_token');
      const storedCoreToken = localStorage.getItem('smartfilterpro_core_token');
      const storedCoreTokenExp = localStorage.getItem('smartfilterpro_core_token_exp');

      if (storedRefreshToken && storedCoreToken) {
        logger.log('✅ Found tokens in localStorage');
        currentTokens = {
          refreshToken: storedRefreshToken,
          accessToken: storedAccessToken || '',
          coreToken: storedCoreToken,
          coreTokenExp: parseInt(storedCoreTokenExp || '0', 10)
        };
      }
    }

    if (!currentTokens) {
      logger.error('❌ Cannot refresh tokens: no tokens available in state or localStorage');
      return null;
    }

    if (!currentTokens.refreshToken) {
      logger.error('❌ Cannot refresh tokens: no refresh_token available');
      logout({ reason: 'Your session expired and could not be renewed. Please sign in again.', notifyBubble: false });
      return null;
    }

    try {
      logger.log('🔄 Force refreshing tokens via:', BUBBLE_ENDPOINTS.REFRESH_TOKEN);
      logger.log('📋 Refresh Debug - Refresh Token:', currentTokens.refreshToken ? `${currentTokens.refreshToken.substring(0, 20)}...` : 'MISSING/EMPTY');
      logger.log('📋 Refresh Debug - Access Token:', currentTokens.accessToken ? `${currentTokens.accessToken.substring(0, 20)}...` : 'MISSING/EMPTY');
      logger.log('📋 Refresh Debug - Core Token:', currentTokens.coreToken ? `${currentTokens.coreToken.substring(0, 20)}...` : 'MISSING/EMPTY');

      // Temporarily remove the Authorization header to prevent Bubble from validating the expired access token
      // The refresh_token in the request body should be sufficient for authentication
      const savedAuthHeader = bubbleAPI.defaults.headers.common['Authorization'];
      delete bubbleAPI.defaults.headers.common['Authorization'];
      logger.log('🔓 Temporarily removed Authorization header for refresh request');

      try {
        const response = await bubbleAPI.post(BUBBLE_ENDPOINTS.REFRESH_TOKEN, {
          refresh_token: currentTokens.refreshToken
        });

        // Restore the Authorization header (will be updated with new token below)
        if (savedAuthHeader) {
          bubbleAPI.defaults.headers.common['Authorization'] = savedAuthHeader;
        }

        const body = response.data?.response || response.data;

        logger.log('📥 Refresh token response:', {
          hasResponse: !!body,
          hasCoreToken: !!body?.core_token,
          hasAccessToken: !!body?.access_token,
          status: response.data?.response?.status
        });

        // Check for nested error in refresh response
        if (response.data?.response?.status && response.data.response.status !== 200) {
          const errorBody = response.data.response.Body;
          let errorMsg = 'Token refresh failed';
          try {
            const parsed = typeof errorBody === 'string' ? JSON.parse(errorBody) : errorBody;
            errorMsg = parsed.message || parsed.error || errorMsg;
          } catch (e) {
            // ignore parse error
          }
          logger.error('❌ Refresh endpoint returned error:', errorMsg);
          throw new Error(errorMsg);
        }

        // Refresh endpoint might only return access_token (not core_token)
        // If core_token is missing, keep the existing one
        if (!body.access_token && !body.core_token) {
          throw new Error('Failed to refresh: no access_token or core_token in response');
        }

        if (!body.access_token) {
          logger.warn('⚠️  Refresh response missing access_token, using core_token as fallback');
        }

        if (!body.core_token) {
          logger.warn('⚠️  Refresh response missing core_token, keeping existing core_token');
        }

        // Update tokens (use new tokens if provided, otherwise keep existing)
        const updatedTokens: TokenData = {
          ...currentTokens,
          coreToken: body.core_token || currentTokens.coreToken, // Keep existing if not provided
          coreTokenExp: body.expires_at || body.exp || currentTokens.coreTokenExp,
          accessToken: body.access_token || body.core_token || currentTokens.accessToken
        };

        setTokens(updatedTokens);

        // Only update Railway token if we got a new core_token
        if (body.core_token) {
          setRailwayAuthToken(body.core_token);
        }

        // Update Bubble access token with new access_token
        setBubbleAccessToken(updatedTokens.accessToken);

        // Update localStorage
        if (body.core_token) {
          localStorage.setItem('smartfilterpro_core_token', body.core_token);
        }
        if (body.expires_at || body.exp) {
          localStorage.setItem('smartfilterpro_core_token_exp', String(body.expires_at || body.exp || 0));
        }
        localStorage.setItem('smartfilterpro_access_token', updatedTokens.accessToken);

        logger.log('✅ Tokens refreshed successfully');
        // Return both token classes so callers can pick the right one:
        // Bubble retries need accessToken; Core retries need coreToken.
        return {
          accessToken: updatedTokens.accessToken,
          coreToken: body.core_token || null,
        };
      } catch (refreshError) {
        // Restore the Authorization header even if refresh failed
        if (savedAuthHeader) {
          bubbleAPI.defaults.headers.common['Authorization'] = savedAuthHeader;
        }
        throw refreshError; // Re-throw to be caught by outer catch
      }
    } catch (err) {
      // Sign out only when Bubble rejected the refresh token. If the request
      // did not get through (no signal, timeout, Bubble down), the stored
      // session stays and the next call tries again.
      if (isSessionRejected(err)) {
        logger.error('❌ Bubble rejected the refresh token - signing out:', err);
        logout({ reason: 'Your session was rejected when it was renewed. Please sign in again.', notifyBubble: false });
      } else {
        logger.warn('⚠️  Token refresh did not get through; keeping the session:', err);
      }
      return null;
    }
  };

  const logout = (options: LogoutOptions = {}) => {
    const { reason = null, notifyBubble = true } = options;
    // On a deliberate sign-out, capture who is signing out (and their Bubble
    // token) before the local state is cleared, then tell Bubble in the
    // background so it ends the Bubble session too. Fire-and-forget: the
    // dashboard signs out at once either way. Automatic sign-outs skip this.
    const signingOutUserId = user?.id || localStorage.getItem('smartfilterpro_user_id');
    const bubbleAccessToken = tokens?.accessToken || localStorage.getItem('smartfilterpro_access_token');
    if (notifyBubble && signingOutUserId) {
      void notifyBubbleLogout(signingOutUserId, bubbleAccessToken);
    }

    setUser(null);
    setTokens(null);
    setRailwayAuthToken(null);
    setBubbleAccessToken(null);
    localStorage.removeItem('smartfilterpro_user_id');
    localStorage.removeItem('smartfilterpro_access_token');
    localStorage.removeItem('smartfilterpro_refresh_token');
    localStorage.removeItem('smartfilterpro_core_token');
    localStorage.removeItem('smartfilterpro_core_token_exp');
    localStorage.removeItem('smartfilterpro_token');
    localStorage.removeItem('smartfilterpro_user_data');
    setError(reason);
  };

  // Register token refresh callback with API client.
  // The callback is consumed by the Core (railway) 401 interceptor, so it must
  // resolve with the refreshed CORE token — never the Bubble access token.
  useEffect(() => {
    // Use a wrapper to avoid stale closure issues
    const refreshWrapper = async (): Promise<string | null> => {
      const refreshed = await forceRefreshTokens();
      // If the refresh response did not include a new core_token, return null
      // so the interceptor skips the retry instead of re-sending a token of
      // the wrong class (or the same core token that just got a 401).
      return refreshed?.coreToken ?? null;
    };
    setTokenRefreshCallback(refreshWrapper);
    return () => setTokenRefreshCallback(null);
  }, [tokens]);

  // Proactive token refresh to prevent session expiry during idle periods
  useEffect(() => {
    if (!tokens || !tokens.refreshToken) {
      return;
    }

    // Refresh tokens 5 minutes before expiry
    const REFRESH_BUFFER_MS = 5 * 60 * 1000;
    // Check every 2 minutes
    const CHECK_INTERVAL_MS = 2 * 60 * 1000;

    const checkAndRefreshTokens = async () => {
      if (!tokens || !tokens.coreTokenExp) {
        return;
      }

      const nowMs = Date.now();
      const timeUntilExpiry = tokens.coreTokenExp - nowMs;

      // If token is expired or will expire within the buffer period, refresh it
      if (timeUntilExpiry < REFRESH_BUFFER_MS) {
        logger.log('⏰ Proactive refresh: Token expiring soon, refreshing...', {
          timeUntilExpiryMinutes: Math.floor(timeUntilExpiry / (60 * 1000)),
          isExpired: timeUntilExpiry <= 0
        });
        await forceRefreshTokens();
      }
    };

    // Set up periodic check interval
    const intervalId = setInterval(checkAndRefreshTokens, CHECK_INTERVAL_MS);
    logger.log('🔄 Proactive token refresh interval started (every 2 minutes)');

    // Also check immediately on mount
    checkAndRefreshTokens();

    return () => {
      clearInterval(intervalId);
      logger.log('🔄 Proactive token refresh interval stopped');
    };
  }, [tokens?.coreTokenExp, tokens?.refreshToken]);

  // Refresh tokens when user returns to the tab after being away
  useEffect(() => {
    if (!tokens || !tokens.refreshToken) {
      return;
    }

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // User returned to tab - check if tokens need refresh
        const nowMs = Date.now();
        const REFRESH_BUFFER_MS = 5 * 60 * 1000;
        const storedExp = localStorage.getItem('smartfilterpro_core_token_exp');
        const tokenExpMs = storedExp ? parseInt(storedExp, 10) : (tokens?.coreTokenExp || 0);
        const timeUntilExpiry = tokenExpMs - nowMs;

        if (timeUntilExpiry < REFRESH_BUFFER_MS) {
          logger.log('👁️ Tab visible: Token expiring soon, refreshing...', {
            timeUntilExpiryMinutes: Math.floor(timeUntilExpiry / (60 * 1000)),
            isExpired: timeUntilExpiry <= 0
          });
          await forceRefreshTokens();
        } else {
          logger.log('👁️ Tab visible: Token still valid', {
            timeUntilExpiryMinutes: Math.floor(timeUntilExpiry / (60 * 1000))
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    logger.log('👁️ Visibility change listener registered for token refresh');

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [tokens?.refreshToken]);

  const value: AuthContextType = {
    user,
    loading,
    error,
    loginWithPassword,
    login,
    logout,
    isAuthenticated: !!user,
    getCoreToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
